/**
 * Infrastructure blueprint generator
 * Creates downloadable configuration files based on wizard settings
 */

import type { WizardState } from '@/stores/wizardStore';

export type BlueprintFormat = 'docker-compose' | 'kubernetes' | 'terraform' | 'ansible';

export type Blueprint = {
  id: string;
  name: string;
  description: string;
  format: BlueprintFormat;
  icon: string;
  content: string;
  filename: string;
};

export type DockerContainer = {
  modelId: string;
  modelName: string;
  imageUrl: string;
  dockerCommand: string;
  requirements: {
    gpus: number;
    memory: string;
    storage: string;
  };
};

/**
 * Generate Docker Compose blueprint
 */
function generateDockerCompose(state: WizardState): Blueprint {
  const models = state.modelSelections.selectedModelIds;
  const services = models.map((modelId, idx) => {
    const personas = state.personas.filter(p => 
      state.modelSelections.modelToPersonas[modelId]?.includes(p.id)
    );
    
    return `  ${modelId.replace(/[^a-z0-9]/gi, '-')}:
    image: vllm/vllm-openai:latest
    container_name: ${modelId.replace(/[^a-z0-9]/gi, '-')}
    ports:
      - "${8000 + idx}:8000"
    environment:
      - MODEL_NAME=${modelId}
      - TENSOR_PARALLEL_SIZE=1
      - GPU_MEMORY_UTILIZATION=0.90
    deploy:
      resources:
        reservations:
          devices:
            - driver: nvidia
              count: 1
              capabilities: [gpu]
    volumes:
      - model-cache-${idx}:/root/.cache
    command: --model ${modelId} --served-model-name ${modelId}`;
  }).join('\n\n');

  const volumes = models.map((_, idx) => `  model-cache-${idx}:`).join('\n');

  const content = `version: '3.8'

services:
${services}

volumes:
${volumes}

# Generated for AI Infrastructure
# Total personas: ${state.personas.length}
# Models: ${models.length}
# Data Sensitivity: ${state.environment.regulatorySensitivity}
`;

  return {
    id: 'docker-compose',
    name: 'Docker Compose',
    description: 'Multi-container deployment with VLLM',
    format: 'docker-compose',
    icon: 'Package',
    content,
    filename: 'docker-compose.yml',
  };
}

/**
 * Generate Kubernetes deployment blueprint
 */
function generateKubernetes(state: WizardState): Blueprint {
  const models = state.modelSelections.selectedModelIds;
  const deployments = models.map(modelId => {
    const safeName = modelId.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    
    return `---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: ${safeName}
  labels:
    app: ai-model
    model: ${safeName}
spec:
  replicas: 2
  selector:
    matchLabels:
      app: ${safeName}
  template:
    metadata:
      labels:
        app: ${safeName}
    spec:
      containers:
      - name: vllm
        image: vllm/vllm-openai:latest
        args:
          - --model
          - ${modelId}
          - --served-model-name
          - ${modelId}
          - --tensor-parallel-size
          - "1"
        ports:
        - containerPort: 8000
          name: http
        resources:
          limits:
            nvidia.com/gpu: 1
            memory: 32Gi
          requests:
            nvidia.com/gpu: 1
            memory: 24Gi
        env:
        - name: MODEL_NAME
          value: ${modelId}
        - name: GPU_MEMORY_UTILIZATION
          value: "0.90"
        volumeMounts:
        - name: cache
          mountPath: /root/.cache
      volumes:
      - name: cache
        emptyDir: {}
---
apiVersion: v1
kind: Service
metadata:
  name: ${safeName}
spec:
  selector:
    app: ${safeName}
  ports:
  - port: 8000
    targetPort: 8000
    name: http
  type: LoadBalancer`;
  }).join('\n\n');

  const content = `# Kubernetes deployment for AI Infrastructure
# Data Sensitivity: ${state.environment.regulatorySensitivity}
# Models: ${models.length}

${deployments}
`;

  return {
    id: 'kubernetes',
    name: 'Kubernetes',
    description: 'Production-ready K8s manifests',
    format: 'kubernetes',
    icon: 'Cloud',
    content,
    filename: 'k8s-deployment.yaml',
  };
}

/**
 * Generate Terraform IaC blueprint
 */
function generateTerraform(state: WizardState): Blueprint {
  const totalGpus = state.modelSelections.selectedModelIds.length * 2;
  
  const content = `# Terraform Infrastructure as Code
# Generated for AI Infrastructure
# Data Sensitivity: ${state.environment.regulatorySensitivity}

terraform {
  required_version = ">= 1.0"
  required_providers {
    aws = {
      source  = "hashicorp/aws"
      version = "~> 5.0"
    }
  }
}

provider "aws" {
  region = "us-east-1"
}

# VPC Configuration
resource "aws_vpc" "ai_vpc" {
  cidr_block           = "10.0.0.0/16"
  enable_dns_hostnames = true
  enable_dns_support   = true

  tags = {
    Name        = "ai-infrastructure-vpc"
    Environment = "production"
    ManagedBy   = "terraform"
  }
}

# GPU Instance Configuration
resource "aws_instance" "gpu_nodes" {
  count         = ${Math.ceil(totalGpus / 4)}
  ami           = data.aws_ami.ubuntu_gpu.id
  instance_type = "p3.8xlarge"  # 4x V100 GPUs

  vpc_security_group_ids = [aws_security_group.ai_sg.id]
  subnet_id              = aws_subnet.public.id

  root_block_device {
    volume_size = 500
    volume_type = "gp3"
  }

  user_data = <<-EOF
    #!/bin/bash
    # Install NVIDIA drivers
    sudo apt-get update
    sudo apt-get install -y nvidia-driver-525
    
    # Install Docker
    curl -fsSL https://get.docker.com -o get-docker.sh
    sh get-docker.sh
    
    # Install NVIDIA Container Toolkit
    distribution=$(. /etc/os-release;echo $ID$VERSION_ID)
    curl -s -L https://nvidia.github.io/nvidia-docker/gpgkey | sudo apt-key add -
    curl -s -L https://nvidia.github.io/nvidia-docker/$distribution/nvidia-docker.list | sudo tee /etc/apt/sources.list.d/nvidia-docker.list
    sudo apt-get update && sudo apt-get install -y nvidia-container-toolkit
    sudo systemctl restart docker
  EOF

  tags = {
    Name        = "ai-gpu-node-\${count.index + 1}"
    Environment = "production"
  }
}

# Security Group
resource "aws_security_group" "ai_sg" {
  name        = "ai-infrastructure-sg"
  description = "Security group for AI infrastructure"
  vpc_id      = aws_vpc.ai_vpc.id

  ingress {
    from_port   = 8000
    to_port     = 8100
    protocol    = "tcp"
    cidr_blocks = ["10.0.0.0/16"]
    description = "API endpoints"
  }

  egress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = ["0.0.0.0/0"]
  }

  tags = {
    Name = "ai-infrastructure-sg"
  }
}

# Data source for Ubuntu AMI with GPU support
data "aws_ami" "ubuntu_gpu" {
  most_recent = true
  owners      = ["099720109477"] # Canonical

  filter {
    name   = "name"
    values = ["ubuntu/images/hvm-ssd/ubuntu-jammy-22.04-amd64-server-*"]
  }

  filter {
    name   = "virtualization-type"
    values = ["hvm"]
  }
}

# Outputs
output "gpu_node_ips" {
  value = aws_instance.gpu_nodes[*].public_ip
  description = "Public IPs of GPU nodes"
}

output "vpc_id" {
  value = aws_vpc.ai_vpc.id
  description = "VPC ID"
}
`;

  return {
    id: 'terraform',
    name: 'Terraform',
    description: 'Infrastructure as Code for AWS',
    format: 'terraform',
    icon: 'FileCode',
    content,
    filename: 'main.tf',
  };
}

/**
 * Generate Ansible playbook
 */
function generateAnsible(state: WizardState): Blueprint {
  const models = state.modelSelections.selectedModelIds;
  
  const content = `---
# Ansible Playbook for AI Infrastructure Setup
# Generated for AI Infrastructure
# Models: ${models.length}

- name: Setup AI Infrastructure
  hosts: gpu_nodes
  become: yes
  vars:
    docker_compose_version: "2.23.0"
    models:
${models.map(m => `      - ${m}`).join('\n')}

  tasks:
    - name: Update apt cache
      apt:
        update_cache: yes
        cache_valid_time: 3600

    - name: Install required packages
      apt:
        name:
          - apt-transport-https
          - ca-certificates
          - curl
          - gnupg
          - lsb-release
          - python3-pip
        state: present

    - name: Install NVIDIA drivers
      apt:
        name: nvidia-driver-525
        state: present
      register: nvidia_install

    - name: Reboot if NVIDIA drivers were installed
      reboot:
        reboot_timeout: 600
      when: nvidia_install.changed

    - name: Add Docker GPG key
      apt_key:
        url: https://download.docker.com/linux/ubuntu/gpg
        state: present

    - name: Add Docker repository
      apt_repository:
        repo: "deb [arch=amd64] https://download.docker.com/linux/ubuntu {{ ansible_distribution_release }} stable"
        state: present

    - name: Install Docker
      apt:
        name:
          - docker-ce
          - docker-ce-cli
          - containerd.io
        state: present

    - name: Install NVIDIA Container Toolkit
      block:
        - name: Add NVIDIA Docker GPG key
          apt_key:
            url: https://nvidia.github.io/nvidia-docker/gpgkey
            state: present

        - name: Add NVIDIA Docker repository
          apt_repository:
            repo: "deb https://nvidia.github.io/nvidia-docker/{{ ansible_distribution | lower }}{{ ansible_distribution_version }}/amd64 /"
            state: present

        - name: Install nvidia-container-toolkit
          apt:
            name: nvidia-container-toolkit
            state: present
            update_cache: yes

    - name: Restart Docker service
      systemd:
        name: docker
        state: restarted
        enabled: yes

    - name: Install Docker Compose
      get_url:
        url: "https://github.com/docker/compose/releases/download/v{{ docker_compose_version }}/docker-compose-linux-x86_64"
        dest: /usr/local/bin/docker-compose
        mode: '0755'

    - name: Create application directory
      file:
        path: /opt/ai-infrastructure
        state: directory
        mode: '0755'

    - name: Deploy Docker Compose file
      copy:
        src: docker-compose.yml
        dest: /opt/ai-infrastructure/docker-compose.yml
        mode: '0644'

    - name: Start AI services
      community.docker.docker_compose:
        project_src: /opt/ai-infrastructure
        state: present
        pull: yes

    - name: Wait for services to be healthy
      uri:
        url: "http://localhost:{{ 8000 + item.0 }}/health"
        status_code: 200
      loop: "{{ models | zip(range(models | length)) | list }}"
      retries: 30
      delay: 10

    - name: Display service endpoints
      debug:
        msg: "Model {{ item.1 }} is available at http://{{ ansible_default_ipv4.address }}:{{ 8000 + item.0 }}"
      loop: "{{ models | zip(range(models | length)) | list }}"
`;

  return {
    id: 'ansible',
    name: 'Ansible',
    description: 'Configuration management playbook',
    format: 'ansible',
    icon: 'Settings',
    content,
    filename: 'playbook.yml',
  };
}

/**
 * Generate all blueprints for wizard state
 */
export function generateBlueprints(state: WizardState): Blueprint[] {
  if (state.modelSelections.selectedModelIds.length === 0) {
    return [];
  }

  return [
    generateDockerCompose(state),
    generateKubernetes(state),
    generateTerraform(state),
    generateAnsible(state),
  ];
}

/**
 * Get Docker container information for selected models
 */
export function getDockerContainers(state: WizardState): DockerContainer[] {
  return state.modelSelections.selectedModelIds.map(modelId => {
    // Estimate requirements based on model
    const isLarge = modelId.includes('70b') || modelId.includes('405b');
    const isMedium = modelId.includes('13b') || modelId.includes('34b');
    
    return {
      modelId,
      modelName: modelId,
      imageUrl: 'vllm/vllm-openai:latest',
      dockerCommand: `docker run --gpus all \\
  -p 8000:8000 \\
  -v ~/.cache/huggingface:/root/.cache/huggingface \\
  --env "HF_TOKEN=your_token_here" \\
  vllm/vllm-openai:latest \\
  --model ${modelId} \\
  --tensor-parallel-size ${isLarge ? '4' : isMedium ? '2' : '1'}`,
      requirements: {
        gpus: isLarge ? 4 : isMedium ? 2 : 1,
        memory: isLarge ? '160GB' : isMedium ? '80GB' : '40GB',
        storage: '200GB',
      },
    };
  });
}

/**
 * Download blueprint as file
 */
export function downloadBlueprint(blueprint: Blueprint) {
  const blob = new Blob([blueprint.content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = blueprint.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
